import { Link } from 'react-router-dom';
import { getCards, getTransactions } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { db } from '@/mocks/db';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, Initials, ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { EmptyState, LoadingRegion, Notice, RowSkeleton } from '@/components/feedback';
import { formatDayGroup, statusLabel } from '@/services/formatters';

/**
 * The lighter mobile entry points.
 *
 * Each one is a real, honest screen rather than a stub: it either shows the
 * mobile-appropriate slice of its module, or states plainly that the work
 * belongs on SME Web / depends on an unconfirmed provider capability, and
 * hands off with context.
 */

/* ------------------------------------------------------------------- team */

/**
 * TRACE:WR-127-05 — mobile team view is read-focused; dense
 * permission administration hands off to SME Web with a stated reason.
 */
export function TeamPage() {
  const resolved = resolveContext();
  if (!resolved) return null;

  const members = db().users.filter((u) => u.companyIds.includes(resolved.company.id));

  const roleLabel: Record<string, string> = {
    role_owner: 'Business owner',
    role_finance: 'Finance administrator',
    role_approver: 'Payment approver',
    role_employee: 'Employee / cardholder',
    role_readonly: 'Read-only finance user',
  };

  return (
    <>
      <MobileHeader title="Team" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {members.map((m) => (
              <li key={m.id}>
                <ListRow
                  leading={<Initials name={m.name} />}
                  title={m.name}
                  meta={`${roleLabel[m.roleId] ?? m.roleId} · ${m.email}`}
                  end={
                    <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                      {m.status !== 'active' ? <StatusBadge status={m.status} /> : null}
                      {m.permissionValidUntil ? (
                        <span className="tm-hint">Access ends {formatDayGroup(m.permissionValidUntil)}</span>
                      ) : null}
                    </span>
                  }
                  ariaLabel={`${m.name}, ${roleLabel[m.roleId] ?? m.roleId}, ${m.status}`}
                />
              </li>
            ))}
          </ul>
        </div>

        <Notice tone="info" title="Changing access is on the web app">
          Granting permissions spans roles, accounts and thresholds at once. Editing that safely
          needs the full matrix view, so it stays on SME Web.
        </Notice>

        <Link
          to="/mobile/continue-on-web?task=team-permissions"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Manage team on web
        </Link>
      </div>
    </>
  );
}

/* -------------------------------------------------------------- documents */

export function DocumentsPage() {
  const resolved = resolveContext();
  if (!resolved) return null;

  const onboarding = resolved.company.status === 'onboarding' || resolved.company.status === 'review';

  const documents = [
    { id: 'doc_incorp', name: 'Certificate of incorporation', status: 'approved' },
    { id: 'doc_address', name: 'Proof of business address', status: onboarding ? 'pending' : 'approved' },
    { id: 'doc_dir_id', name: 'Director identity — A. Harbour', status: onboarding ? 'information-required' : 'approved' },
    { id: 'doc_ownership', name: 'Ownership structure', status: 'approved' },
  ];

  return (
    <>
      <MobileHeader title="Documents" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        {onboarding ? (
          <Notice tone="warning" title="We still need something from you">
            {resolved.company.statusReason ?? 'One or more documents need attention before your account is fully open.'}
          </Notice>
        ) : null}

        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {documents.map((d) => (
              <li key={d.id}>
                <ListRow
                  title={d.name}
                  meta={
                    d.status === 'information-required'
                      ? 'We need a clearer copy'
                      : d.status === 'pending'
                        ? 'Being reviewed'
                        : 'Accepted'
                  }
                  end={<StatusBadge status={d.status} label={statusLabel(d.status)} />}
                  ariaLabel={`${d.name}, ${statusLabel(d.status)}`}
                />
              </li>
            ))}
          </ul>
        </div>

        <Notice tone="info" title="Uploading documents">
          Uploading and re-submitting verification documents needs the full desktop flow so you can
          check the file before it is sent.
        </Notice>
        <Link
          to="/mobile/continue-on-web?task=exports"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Upload on web
        </Link>
      </div>
    </>
  );
}

/* ------------------------------------------------------ onboarding status */

export function OnboardingStatusPage() {
  const resolved = resolveContext();
  if (!resolved) return null;

  const steps = [
    { id: 'company', label: 'Company details', done: true },
    { id: 'directors', label: 'Directors and owners', done: true },
    { id: 'documents', label: 'Verification documents', done: resolved.company.status === 'active' },
    { id: 'account', label: 'Account opened', done: resolved.company.status === 'active' },
  ];

  return (
    <>
      <MobileHeader title="Application status" backTo="/mobile/home" backLabel="Home" />
      <div className="tm-stack">
        <div className="tm-card">
          <p className="tm-eyebrow">{resolved.company.tradingName}</p>
          <h2 className="tm-page-title" style={{ marginBlockEnd: 6 }}>
            {resolved.company.status === 'active' ? 'You are all set' : 'Application in progress'}
          </h2>
          <StatusBadge status={resolved.company.status} />
          {resolved.company.statusReason ? (
            <p className="tm-muted" style={{ marginBlockStart: 10, marginBlockEnd: 0 }}>
              {resolved.company.statusReason}
            </p>
          ) : null}
        </div>

        <section aria-labelledby="onb-steps" className="tm-card">
          <SectionHeader title="Your progress" id="onb-steps" />
          <ol className="tm-timeline" style={{ marginBlockStart: 12 }}>
            {steps.map((s) => (
              <li key={s.id} className="tm-timeline__item">
                <div className="tm-between">
                  <strong>{s.label}</strong>
                  <StatusBadge
                    status={s.done ? 'completed' : 'pending'}
                    label={s.done ? 'Done' : 'Waiting'}
                  />
                </div>
              </li>
            ))}
          </ol>
        </section>

        <Link to="/mobile/documents" className="tm-btn tm-btn--block">
          View your documents
        </Link>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- tax */

export function TaxPage() {
  return (
    <>
      <MobileHeader title="Tax calendar" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            <li>
              <ListRow title="VAT return" meta="Quarter ending 30 September" end={<StatusBadge status="scheduled" label="Due 7 Nov" tone="info" />} />
            </li>
            <li>
              <ListRow title="PAYE" meta="Monthly" end={<StatusBadge status="scheduled" label="Due 22 Aug" tone="info" />} />
            </li>
            <li>
              <ListRow title="Corporation tax" meta="Year ending 31 March" end={<StatusBadge status="scheduled" label="Due 1 Jan" tone="neutral" />} />
            </li>
          </ul>
        </div>
        <Notice tone="info" title="Dates are indicative">
          These are typical deadlines for a UK company and are shown for planning only. Confirm your
          own dates with your accountant or HMRC.
        </Notice>
      </div>
    </>
  );
}

/* ------------------------------------------------- connected accounting */

export function ConnectedAccountsPage() {
  return (
    <>
      <MobileHeader title="Connected accounting" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            <li>
              <ListRow
                title="Bookkeeping sync"
                meta="Last synced today at 08:40"
                end={<StatusBadge status="active" label="Connected" tone="success" />}
              />
            </li>
            <li>
              <ListRow
                title="Unmatched transactions"
                meta="Items needing a category or receipt"
                end={<span className="tm-count">4</span>}
              />
            </li>
          </ul>
        </div>
        <Notice tone="info" title="Reconciliation is on the web app">
          Matching many transactions at once needs a side-by-side view, so reconciliation stays on
          SME Web. Mobile shows the sync status so you know where things stand.
        </Notice>
        <Link
          to="/mobile/continue-on-web?task=exports"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Reconcile on web
        </Link>
      </div>
    </>
  );
}

/* --------------------------------------------------------------- expenses */

/**
 * TRACE:TT-R137-01 — a distinct Expenses entry point exists at /mobile/expenses.
 * TRACE:TT-R137-02 — it shows expense and receipt content, not the finance
 * dashboard.
 * TRACE:TT-R155-T10-01 — expense management is presented as a separate module from
 * digital banking, and receipt capture does not dominate the banking surface.
 */
export function ExpensesPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const cardSpend = useAsync(async () => {
    if (!companyId || !userId) return [];
    return [
      ...(await getTransactions(companyId, userId, {}, null, 50)).data.items.filter(
        (t) => t.type === 'card-purchase',
      ),
    ];
  }, [companyId, userId]);

  const cards = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getCards(companyId, userId)).data;
  }, [companyId, userId]);

  if (!resolved) return null;

  return (
    <>
      <MobileHeader title="Expenses & receipts" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <Notice tone="info" title="A separate module">
          Expense management is a separate product from your digital banking. This entry point shows
          card spend that may need a receipt.
        </Notice>

        <section aria-labelledby="exp-missing">
          <SectionHeader title="Needs a receipt" id="exp-missing" />
          {cardSpend.status === 'loading' ? (
            <LoadingRegion label="Loading card spend">
              <div className="tm-card tm-card--flush">
                <RowSkeleton count={3} />
              </div>
            </LoadingRegion>
          ) : (cardSpend.data ?? []).length === 0 ? (
            <div className="tm-card">
              <EmptyState title="Nothing outstanding" body="Every card payment has what it needs." />
            </div>
          ) : (
            <div className="tm-card tm-card--flush">
              <ul className="tm-rows">
                {(cardSpend.data ?? []).slice(0, 6).map((t) => {
                  const card = (cards.data ?? []).find((c) => c.id === t.relatedCardId);
                  return (
                    <li key={t.id}>
                      <ListRow
                        to={`/mobile/transactions/${t.id}`}
                        title={t.merchantName ?? t.counterparty}
                        meta={`${formatDayGroup(t.occurredAt)}${card ? ` · card ${card.last4}` : ''}`}
                        end={<Amount minorUnits={t.amount} currency={t.currency} />}
                        ariaLabel={`${t.merchantName ?? t.counterparty}, card payment, may need a receipt`}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>

        <Notice tone="info" title="Submitting a claim">
          Capturing a receipt and submitting a claim happens in the expense module. Whether a claim
          is triggered by a card authorisation or an IBAN transfer is an open decision recorded in
          the project decision log.
        </Notice>

        <Link
          to="/mobile/continue-on-web?task=exports"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Open the expense module on web
        </Link>
      </div>
    </>
  );
}

/* ------------------------------------------------------------- invoicing */

/**
 * TRACE:TT-R072-K1-08 — invoicing concepts appear on SME Mobile HTML at an
 * appropriate depth: what is owed, what is overdue and what has been paid,
 * using the same vocabulary as the web surface. Raising and editing an invoice
 * is deliberately not reproduced on a phone.
 */
export function InvoicesPage() {
  const resolved = resolveContext();
  if (!resolved) return null;

  const currency = resolved.company.baseCurrency;
  const invoices = [
    { id: 'INV-4471', payer: 'Ashgrove Packaging Ltd', amount: 1_250_000, state: 'sent' },
    { id: 'INV-4468', payer: 'Braithwaite Retail Group', amount: 1_845_000, state: 'paid' },
    { id: 'INV-4455', payer: 'Kestrel Foods Ltd', amount: 428_000, state: 'overdue' },
    { id: 'INV-4440', payer: 'Stonebridge Cafes', amount: 96_400, state: 'paid' },
  ] as const;

  const outstanding = invoices
    .filter((i) => i.state !== 'paid')
    .reduce((sum, i) => sum + i.amount, 0);
  const overdue = invoices
    .filter((i) => i.state === 'overdue')
    .reduce((sum, i) => sum + i.amount, 0);

  const tone = (state: string) =>
    state === 'paid' ? 'success' : state === 'overdue' ? 'danger' : 'info';

  return (
    <>
      <MobileHeader title="Invoices" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <div className="tm-card">
          <p className="tm-eyebrow">Outstanding</p>
          <Amount minorUnits={outstanding} currency={currency} size="hero" />
          <p className="tm-muted" style={{ margin: '8px 0 0' }}>
            of which <Amount minorUnits={overdue} currency={currency} /> is overdue
          </p>
        </div>

        <section aria-labelledby="inv-list">
          <SectionHeader title="Receivables" id="inv-list" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {invoices.map((i) => (
                <li key={i.id}>
                  <ListRow
                    title={i.payer}
                    meta={i.id}
                    end={
                      <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                        <Amount minorUnits={i.amount} currency={currency} />
                        <StatusBadge status={i.state} label={statusLabel(i.state)} tone={tone(i.state)} />
                      </span>
                    }
                    ariaLabel={`${i.payer}, invoice ${i.id}, ${i.state}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Notice tone="info" title="Raising invoices is on the web app">
          Creating, editing and sending an invoice needs line items, tax treatment and a document
          preview, so it stays on SME Web. Mobile shows what you are owed.
        </Notice>
        <Link
          to="/mobile/continue-on-web?task=exports"
          className="tm-btn tm-btn--secondary tm-btn--block"
        >
          Manage invoices on web
        </Link>
      </div>
    </>
  );
}

/* ----------------------------------------------- company profile and API */

/**
 * TRACE:XS-SMEM-TT-R088 — the source feedback asks what "company profile" and
 * "integrations" actually mean and whether the fields are clear. The source
 * text is a question, not a specification, so nothing is invented here: the
 * surface states the two concepts distinctly and records the open question
 * rather than guessing at a field list.
 */
export function CompanyProfilePage() {
  const resolved = resolveContext();
  if (!resolved) return null;

  return (
    <>
      <MobileHeader title="Company profile" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <section className="tm-card" aria-labelledby="cp-profile">
          <SectionHeader title="Company profile" id="cp-profile" />
          <p className="tm-muted" style={{ marginBlockStart: 6 }}>
            Who your company is: its legal identity and the details your bank holds.
          </p>
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'Legal name', value: resolved.company.legalName },
                { label: 'Trading name', value: resolved.company.tradingName },
                { label: 'Registration number', value: resolved.company.registrationNumber },
                { label: 'Country', value: resolved.company.country },
                { label: 'Status', value: <StatusBadge status={resolved.company.status} /> },
              ]}
            />
          </div>
        </section>

        <section className="tm-card" aria-labelledby="cp-integrations">
          <SectionHeader title="Integrations" id="cp-integrations" />
          <p className="tm-muted" style={{ marginBlockStart: 6 }}>
            Separately: the other systems connected to your account, such as bookkeeping software.
            This is about connections, not identity.
          </p>
          <div style={{ marginBlockStart: 12 }}>
            <ListRow
              to="/mobile/connected-accounts"
              title="Connected accounting"
              meta="See sync status"
            />
          </div>
        </section>

        <Notice tone="warning" title="Open question recorded">
          The source feedback asks which fields belong under company profile versus integrations and
          notes the current labelling is unclear. That is an open decision awaiting Tribe
          confirmation; the two concepts are kept visibly separate here rather than merged on a
          guess.
        </Notice>
      </div>
    </>
  );
}

/* -------------------------------------------------------- wallets/spaces */

/**
 * TRACE:TT-R134-01 — a "Wallets and spaces" entry point exists in the shell.
 * TRACE:TT-R134-02 — it renders content distinct from Accounts and is
 * explicitly labelled provider-gated rather than inventing capability.
 *
 * The tenant in this build has not purchased digital assets, so the entitlement
 * chain normally hides this route entirely. It exists so the provider-gated
 * state itself is demonstrable and honest when a tenant does enable it.
 */
export function WalletsSpacesPage() {
  return (
    <>
      <MobileHeader title="Wallets and spaces" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <Notice tone="warning" title="Not enabled for your programme">
          Wallets and spaces are a provider-gated capability. Not found in Wallet &amp; Merchant API
          v1; requires confirmation from Issuer Processing, Risk Monitor, Programme Manager APIs or
          Tribe Hub.
        </Notice>

        <div className="tm-card">
          <SectionHeader title="What this would offer" />
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'Spaces', value: 'Ring-fenced pots inside an account' },
                { label: 'Card funding', value: 'Tie a card to a funded space' },
                { label: 'Digital assets', value: 'Provider dependent' },
              ]}
            />
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 12 }}>
            This is deliberately distinct from Accounts: an account holds a balance and payment
            identifiers, while a space subdivides one.
          </p>
        </div>
      </div>
    </>
  );
}
